var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { parse, TypeKind, } from "graphql";
import { QUERY_TYPES } from "ra-data-graphql";
import { DELETE, GET_LIST, GET_MANY, GET_MANY_REFERENCE } from "react-admin";
import getFinalType from "./utils/getFinalType";
import * as gqlTypes from "./utils/gqlTypes";
import isList from "./utils/isList";
import isRequired from "./utils/isRequired";
export var buildFields = function (introspectionResults) { return function (fields, maxNestingDepth, depth) {
    if (maxNestingDepth === void 0) { maxNestingDepth = 1; }
    if (depth === void 0) { depth = 0; }
    return fields.reduce(function (acc, field) {
        var type = getFinalType(field.type);
        if (type.name.startsWith("_")) {
            return acc;
        }
        // skip if the field has mandatory args
        if (field.args.some(function (arg) { return arg.type.kind === "NON_NULL"; })) {
            return acc;
        }
        if (type.kind !== TypeKind.OBJECT) {
            return __spreadArrays(acc, [gqlTypes.field(gqlTypes.name(field.name))]);
        }
        var linkedResource = introspectionResults.resources.find(function (r) { return r.type.name === type.name; });
        if (linkedResource) {
            return __spreadArrays(acc, [
                gqlTypes.field(gqlTypes.name(field.name), {
                    selectionSet: gqlTypes.selectionSet([
                        gqlTypes.field(gqlTypes.name("id")),
                    ]),
                }),
            ]);
        }
        var linkedType = introspectionResults.types.find(function (t) { return t.name === type.name; });
        // linkedtypes  (= nested objects) are currently a bit problematic
        // it is handy to have them, but they be slow to fetch
        // we therefore limit the depth
        if (linkedType && depth < maxNestingDepth) {
            return __spreadArrays(acc, [
                gqlTypes.field(gqlTypes.name(field.name), {
                    selectionSet: gqlTypes.selectionSet(buildFields(introspectionResults)(linkedType.fields, maxNestingDepth, depth + 1)),
                }),
            ]);
        }
        return acc;
    }, []);
}; };
export var getArgType = function (arg) {
    var type = getFinalType(arg.type);
    var required = isRequired(arg.type);
    var list = isList(arg.type);
    if (list) {
        if (required) {
            return gqlTypes.listType(gqlTypes.nonNullType(gqlTypes.namedType(gqlTypes.name(type.name))));
        }
        return gqlTypes.listType(gqlTypes.namedType(gqlTypes.name(type.name)));
    }
    if (required) {
        return gqlTypes.nonNullType(gqlTypes.namedType(gqlTypes.name(type.name)));
    }
    return gqlTypes.namedType(gqlTypes.name(type.name));
};
export var buildArgs = function (query, variables) {
    if (variables === void 0) { variables = {}; }
    if (query.args.length === 0) {
        return [];
    }
    var validVariables = Object.keys(variables).filter(function (k) { return typeof variables[k] !== "undefined"; });
    return query.args
        .filter(function (arg) { return validVariables.includes(arg.name); })
        .reduce(function (acc, arg) { return __spreadArrays(acc, [
        gqlTypes.argument(gqlTypes.name(arg.name), gqlTypes.variable(gqlTypes.name(arg.name))),
    ]); }, []);
};
export var buildApolloArgs = function (query, variables) {
    if (variables === void 0) { variables = {}; }
    if (query.args.length === 0) {
        return [];
    }
    var validVariables = Object.keys(variables).filter(function (k) { return typeof variables[k] !== "undefined"; });
    return query.args
        .filter(function (arg) { return validVariables.includes(arg.name); })
        .reduce(function (acc, arg) { return __spreadArrays(acc, [
        gqlTypes.variableDefinition(gqlTypes.variable(gqlTypes.name(arg.name)), getArgType(arg)),
    ]); }, []);
};
//TODO: validate fragment against the schema
var buildFieldsFromFragment = function (fragment, resourceName, fetchType) {
    var _a;
    var parsedFragment = {};
    if (typeof fragment === "object" &&
        fragment.kind &&
        fragment.kind === "Document") {
        parsedFragment = fragment;
    }
    if (typeof fragment === "string") {
        if (!fragment.startsWith("fragment")) {
            fragment = "fragment tmp on " + resourceName + " " + fragment;
        }
        try {
            parsedFragment = parse(fragment);
        }
        catch (e) {
            throw new Error("Invalid fragment given for resource '" + resourceName + "' and fetchType '" + fetchType + "' (" + e.message + ").");
        }
    }
    return (_a = parsedFragment.definitions) === null || _a === void 0 ? void 0 : _a[0].selectionSet.selections;
};
export default (function (introspectionResults) { return function (resource, aorFetchType, variables, fragment) {
    var _a;
    var queryType = resource[aorFetchType];
    if (!queryType) {
        throw new Error("No query or mutation matching aor fetch type " + aorFetchType + " could be found for resource " + resource.type.name);
    }
    var orderBy = variables.orderBy, skip = variables.skip, take = variables.take, countVariables = __rest(variables, ["orderBy", "skip", "take"]);
    var apolloArgs = buildApolloArgs(queryType, variables);
    var args = buildArgs(queryType, variables);
    var countArgs = buildArgs(queryType, countVariables);
    var countName = queryType.name + "Count";
    var supportsCountArgs = ((_a = introspectionResults.queries.find(function (query) { return query.name === countName; })) === null || _a === void 0 ? void 0 : _a.args.length) > 0;
    var fields = fragment
        ? buildFieldsFromFragment(fragment, resource.type.name, aorFetchType)
        : buildFields(introspectionResults)(resource.type.fields);
    if (aorFetchType === GET_LIST ||
        aorFetchType === GET_MANY ||
        aorFetchType === GET_MANY_REFERENCE) {
        return gqlTypes.document([
            gqlTypes.operationDefinition("query", gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name(queryType.name), {
                    alias: gqlTypes.name("items"),
                    arguments: args,
                    selectionSet: gqlTypes.selectionSet(fields),
                }),
                gqlTypes.field(gqlTypes.name(countName), {
                    alias: gqlTypes.name("total"),
                    arguments: supportsCountArgs ? countArgs : undefined,
                }),
            ]), gqlTypes.name(queryType.name), apolloArgs),
        ]);
    }
    if (aorFetchType === DELETE) {
        return gqlTypes.document([
            gqlTypes.operationDefinition("mutation", gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name(queryType.name), {
                    alias: gqlTypes.name("data"),
                    arguments: args,
                    selectionSet: gqlTypes.selectionSet([
                        gqlTypes.field(gqlTypes.name("id")),
                    ]),
                }),
            ]), gqlTypes.name(queryType.name), apolloArgs),
        ]);
    }
    return gqlTypes.document([
        gqlTypes.operationDefinition(QUERY_TYPES.includes(aorFetchType) ? "query" : "mutation", gqlTypes.selectionSet([
            gqlTypes.field(gqlTypes.name(queryType.name), {
                alias: gqlTypes.name("data"),
                arguments: args,
                selectionSet: gqlTypes.selectionSet(fields),
            }),
        ]), gqlTypes.name(queryType.name), apolloArgs),
    ]);
}; });
//# sourceMappingURL=buildGqlQuery.js.map